import {
  Task,
  TaskListItem,
  TaskFilters,
  CreateTaskDto,
  UpdateTaskDto,
  CompleteTaskDto,
  DeleteTaskResult,
  EndDepartureDto,
  ScheduleTaskDto,
  TechnicianTasksResponse,
  UpdateTechnicianCommentDto, StartWorkItemDto } from "@myapp/contract";
import apiClient from "../../../shared/config/axios";
import { Paginated } from "../../../shared/types/IPaginationResponse";

export const taskApi = {
  search: (params: TaskFilters & { page: number }) =>
    apiClient.get<Paginated<TaskListItem>>("/tasks", { params }),

  getById: (id: number) =>
    apiClient.get<Task>(`/tasks/${id}`),

  create: (dto: CreateTaskDto) =>
    apiClient.post<Task>("/tasks", dto),

  update: (id: number, dto: UpdateTaskDto) =>
    apiClient.patch<Task>(`/tasks/${id}`, dto),

  /**
   * "Anfahrt starten" — the server stamps the raw departure instant. `body` only confirms starting
   * an item the office is rescheduling; a plain tap sends none.
   */
  startTravel: (id: number, body?: StartWorkItemDto) =>
    apiClient.post<Task>(`/tasks/${id}/travel/start`, body),

  /** "Arbeit starten" — the server stamps the instant. `body` as on `startTravel`. */
  start: (id: number, body?: StartWorkItemDto) =>
    apiClient.post<Task>(`/tasks/${id}/start`, body),

  /** `Rückgängig` — clears the last stamped instant so the next tap records an honest "now". */
  revertPhase: (id: number) =>
    apiClient.post<Task>(`/tasks/${id}/phase/revert`),

  /** "Abfahrt beenden" — closes the departure phase and the work session. */
  endDeparture: (id: number, dto: EndDepartureDto) =>
    apiClient.post<Task>(`/tasks/${id}/departure/end`, dto),

  complete: (id: number, dto: CompleteTaskDto) =>
    apiClient.patch<Task>(`/tasks/${id}/complete`, dto),

  schedule: (id: number, dto: ScheduleTaskDto) =>
    apiClient.patch<Task>(`/tasks/${id}/schedule`, dto),

  /** Releases the reservation and leaves the task itself alone. Detaches it from its series. */
  unschedule: (id: number) =>
    apiClient.delete<Task>(`/tasks/${id}/schedule`),

  /**
   * `forFullSeries` also removes every LATER occurrence, skipping completed and started ones.
   * The addressed task goes whatever its state; the route is office-only (403 otherwise).
   */
  delete: (id: number, forFullSeries = false) =>
    apiClient.delete<DeleteTaskResult>(`/tasks/${id}`, {
      params: forFullSeries ? { forFullSeries: true } : undefined,
    }),

  getTechnicianTasks: (technicianId: number, params: { from: string; to: string }) =>
    apiClient.get<TechnicianTasksResponse>(`/tasks/technician/${technicianId}`, { params }),

  /** Stores the files and returns their `/static/...` paths — send those back as `attachments`. */
  uploadFiles: (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return apiClient.post<string[]>("/tasks/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // ── Field feedback — the technician's comment and files, during and after the job ──

  /** `null` clears the comment. Assigned technician or office only (403 otherwise). */
  updateTechnicianComment: (id: number, dto: UpdateTechnicianCommentDto) =>
    apiClient.patch<Task>(`/tasks/${id}/technician-comment`, dto),

  /** Stores the files AND records them on the task with the uploader — see the appointment twin. */
  uploadTechnicianAttachments: (id: number, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return apiClient.post<Task>(`/tasks/${id}/technician-attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /** Only the uploader or the office may remove a field attachment (403 otherwise). */
  removeTechnicianAttachment: (id: number, attachmentId: number) =>
    apiClient.delete<Task>(`/tasks/${id}/technician-attachments/${attachmentId}`),
};
